import { useQuery } from '@apollo/client';
import {
    productionOverviewInboxTemplate,
    productionOverviewProductionTemplate,
    productionOverviewReceivedTemplate,
} from 'apollo/templates/production-overview';
import {
    ProductionOverviewCount,
    ProductionOverviewProduction,
    ProductionOverviewReceived,
} from 'models/production-overview';

/** Keep the page reasonably fresh without hammering the backend (counts are cached 60 s there). */
export const OVERVIEW_POLL_INTERVAL_MS = 120000;

type UseProductionOverviewDataInput = {
    fromDate: string;
    toDate: string;
    clinicId?: string;
    /** True while the custom range is incomplete or invalid — no query runs then. */
    skip: boolean;
};

type UseProductionOverviewDataResult = {
    inbox?: ProductionOverviewCount;
    received?: ProductionOverviewReceived;
    production?: ProductionOverviewProduction;
    isLoading: boolean;
    error?: Error;
    /** The new range is still loading, so the counts above are the previous range's (DD-3880). */
    isShowingPreviousRange: boolean;
};

/**
 * The three count queries behind the Overview calendar. Every bucket carries orderCount
 * and actionCount side by side, so the Orders|Actions toggle never refetches — it only
 * switches which field the components display.
 */
export const useProductionOverviewData = ({
    fromDate,
    toDate,
    clinicId,
    skip,
}: UseProductionOverviewDataInput): UseProductionOverviewDataResult => {
    const filter = clinicId ? { fromDate, toDate, clinicId } : { fromDate, toDate };

    const inboxQuery = useQuery(productionOverviewInboxTemplate, {
        variables: clinicId ? { clinicId } : {},
        fetchPolicy: 'cache-and-network',
        pollInterval: OVERVIEW_POLL_INTERVAL_MS,
        skip,
    });

    const receivedQuery = useQuery(productionOverviewReceivedTemplate, {
        variables: { filter },
        fetchPolicy: 'cache-and-network',
        pollInterval: OVERVIEW_POLL_INTERVAL_MS,
        skip,
    });

    const productionQuery = useQuery(productionOverviewProductionTemplate, {
        variables: { filter },
        fetchPolicy: 'cache-and-network',
        pollInterval: OVERVIEW_POLL_INTERVAL_MS,
        skip,
    });

    // While a new range loads, the previous range's counts stay on screen (with its days),
    // so the calendar never flashes empty and can slide to the new range once it is in.
    const receivedData = receivedQuery.data ?? receivedQuery.previousData;
    const productionData = productionQuery.data ?? productionQuery.previousData;

    return {
        inbox: inboxQuery.data?.productionOverviewInbox,
        received: receivedData?.productionOverviewReceived,
        production: productionData?.productionOverviewProduction,
        isLoading: inboxQuery.loading || receivedQuery.loading || productionQuery.loading,
        error: inboxQuery.error || receivedQuery.error || productionQuery.error,
        isShowingPreviousRange: (!receivedQuery.data && !skip) || (!productionQuery.data && !skip),
    };
};
